import { coverFor, jstDateKey, rotationInfo } from './rotation.js';
/**
 * JST 暦日 (y, m1based, d) の正午（+09:00）を指す Date。jstDateKey は TZ 非依存なので**日付ラベルは常に当該日で確定**する。
 * ただしローテーション index（rotation.ts の periodIndex）は Date のローカルフィールド由来で**プロセス TZ 依存**のため、
 * 本番（JST）や UTC 近傍のプロセスでは正しいが、UTC より大きく西の TZ では前日側にずれ得る（rotation.ts から継承した既知の前提）。
 * 正午アンカーはこのずれ幅を最小化するためのもので、TZ 非依存を保証するものではない。
 * @graph-connects none
 */
function jstNoon(year, month, day) {
    const mm = String(month).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    return new Date(`${String(year)}-${mm}-${dd}T12:00:00+09:00`);
}
/**
 * 指定ロスター・役割・日付の当番を導出する（カバー優先 → cadence ローテーション。rotation.ts の共有導出を再利用）。
 * @graph-connects coverFor [calls] 指定日・役割のカバー担当を取得
 * @graph-connects rotationInfo [calls] queue + cadence からその日のローテーション担当を導出
 */
export function dutyOnDate(roster, role, date) {
    const queue = role === 'opetan' ? roster.opetanQueue : roster.reviewerQueue;
    const cadence = role === 'opetan' ? roster.opetanCadence : roster.reviewerCadence;
    return rotationInfo(queue, cadence, coverFor(roster.covers, role, date), date).current;
}
/**
 * 対象日に有効だったロスターを履歴から選ぶ（effectiveFrom の JST 暦日が対象日以前で最新のもの。無ければ null）。
 * 同日に複数変更があれば最後（effectiveFrom が最大）を採用する。ISO 文字列は同形式なら辞書順 = 時刻順。
 * @graph-connects jstDateKey [calls] effectiveFrom と対象日を JST 暦日キーへ整形して比較
 */
export function rosterAsOf(snapshots, date) {
    const targetKey = jstDateKey(date);
    let best = null;
    for (const snap of snapshots) {
        if (jstDateKey(new Date(snap.effectiveFrom)) > targetKey)
            continue;
        if (best === null || snap.effectiveFrom > best.effectiveFrom)
            best = snap;
    }
    return best;
}
/**
 * 指定月（1-based）の各日について当番実績を導出する（担当カレンダー用）。履歴が無い日は opetan/reviewer とも null。
 * 過ぎて掃除されたカバーも、それを設定した当時のイベントに残っているため正しく再現される。
 * @graph-connects rosterAsOf [calls] 各日に有効だったロスターを履歴から選ぶ
 * @graph-connects dutyOnDate [calls] 選んだロスターからその日の当番を導出
 */
export function opsDutyMonth(snapshots, year, month) {
    const daysInMonth = new Date(year, month, 0).getDate(); // month=1based → 0 日目 = 当月末日
    const out = [];
    for (let day = 1; day <= daysInMonth; day++) {
        const date = jstNoon(year, month, day);
        const roster = rosterAsOf(snapshots, date);
        out.push({
            date: jstDateKey(date),
            opetan: roster === null ? null : dutyOnDate(roster, 'opetan', date),
            reviewer: roster === null ? null : dutyOnDate(roster, 'reviewer', date),
            opetanFromCover: roster !== null && coverFor(roster.covers, 'opetan', date) !== null,
            reviewerFromCover: roster !== null && coverFor(roster.covers, 'reviewer', date) !== null,
        });
    }
    return out;
}
//# sourceMappingURL=duty-calendar.js.map